import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getBillingModulePrices } from '../api/pricingApi';
import { userSeatLimitErrorMessage } from '../billing/userSeatLimitMessage';
import {
  createPortalSubscriptionChange,
  createSubscriptionChange,
  previewPortalSubscriptionChange,
  previewSubscriptionChange,
} from '../api/subscriptionChangesApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { formatModuleCode } from '../utils/systemLabels';

function formatModulesList(modules) {
  if (!modules?.length) {
    return '—';
  }

  return modules.map((code) => formatModuleCode(code) || code).join(', ');
}

export function shouldShowDowngradeRefundWarning(preview) {
  if (!preview) {
    return false;
  }

  return preview.effective_policy === 'period_end'
    || ((preview.modules_added || []).length === 0 && (preview.modules_removed || []).length === 0 && preview.change_type === 'downgrade');
}

export function shouldUseImmediatePriceChangeLabel() {
  return false;
}

export function shouldShowManualProrationNotice(preview) {
  if (!preview) {
    return false;
  }

  if (preview.effective_policy === 'period_end') {
    return false;
  }

  const manual = preview.proration_mode === 'manual'
    || (preview.warnings || []).some((warning) => /proration mode is manual/i.test(String(warning)));
  if (!manual) {
    return false;
  }

  return preview.change_type === 'upgrade' || (preview.modules_added || []).length > 0;
}

function isMappedTechnicalPreviewWarning(warning) {
  const text = String(warning || '');
  return /proration mode is manual/i.test(text)
    || /downgrade applies at period end/i.test(text)
    || /module changes apply at the start/i.test(text)
    || /phase 12h/i.test(text)
    || /fáza 12h/i.test(text)
    || /cleanup deferred/i.test(text);
}

export function ChangePreviewPanel({ preview, t }) {
  if (!preview) return null;

  const operatorWarnings = (preview.warnings || []).filter((warning) => !isMappedTechnicalPreviewWarning(warning));

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Typography variant="subtitle2">{t('subscriptionChanges:changeType')}: {t(`subscriptionChanges:${preview.change_type}`)}</Typography>
      <Typography variant="body2">{t('subscriptionChanges:currentPackage')}: {formatModulesList(preview.current?.modules)}</Typography>
      <Typography variant="body2">{t('subscriptionChanges:requestedPackage')}: {formatModulesList(preview.requested?.modules)}</Typography>
      {preview.modules_added?.length > 0 && (
        <Typography variant="body2">{t('subscriptionChanges:modulesAdded')}: {formatModulesList(preview.modules_added)}</Typography>
      )}
      {preview.modules_removed?.length > 0 && (
        <Typography variant="body2">{t('subscriptionChanges:modulesRemoved')}: {formatModulesList(preview.modules_removed)}</Typography>
      )}
      <Typography variant="body2">
        {preview.effective_policy === 'period_end'
          ? t('subscriptionChanges:noMidPeriodCharge')
          : `${t('subscriptionChanges:priceDelta')}: ${preview.price_delta_amount} ${preview.currency}`}
      </Typography>
      {preview.effective_policy === 'period_end' && preview.next_period_amount != null && (
        <Typography variant="body2">
          {t('subscriptionChanges:nextPeriodAmount')}: {preview.next_period_amount} {preview.currency}
        </Typography>
      )}
      <Typography variant="body2">
        {preview.effective_policy === 'immediate_after_payment'
          ? t('subscriptionChanges:effectiveImmediatelyAfterPayment')
          : t('subscriptionChanges:effectiveAtPeriodEnd')}
      </Typography>
      {preview.effective_at && (
        <Typography variant="body2">{t('subscriptionChanges:effectiveAt')}: {new Date(preview.effective_at).toLocaleString()}</Typography>
      )}
      {shouldShowManualProrationNotice(preview) && (
        <Alert severity="info">{t('subscriptionChanges:prorationManualUpgrade')}</Alert>
      )}
      {shouldShowDowngradeRefundWarning(preview) && (
        <Alert severity="warning">{t('subscriptionChanges:noRefundPolicy')}</Alert>
      )}
      {preview.permission_cleanup_required && (
        <Alert severity="info">{t('subscriptionChanges:permissionCleanupRequired')}</Alert>
      )}
      {operatorWarnings.map((warning) => (
        <Alert key={warning} severity="info">{warning}</Alert>
      ))}
    </Stack>
  );
}

export function SubscriptionChangeDialog({
  open,
  onClose,
  subscription,
  customerId = null,
  mode = 'admin',
  initialModules = [],
  initialUsersCount = 10,
  initialBillingInterval = 'monthly',
  onSuccess,
}) {
  const { t } = useTranslation(['subscriptionChanges', 'common', 'billing']);
  const { session } = useAuth();
  const isPortal = mode === 'portal';
  const canCrmOverride = !isPortal && hasPermission(session, 'system.billing.subscription_changes.manage');

  const [moduleOptions, setModuleOptions] = useState([]);
  const [selectedModules, setSelectedModules] = useState(initialModules);
  const [usersCount, setUsersCount] = useState(initialUsersCount);
  const [billingInterval, setBillingInterval] = useState(initialBillingInterval);
  const [includeSupport, setIncludeSupport] = useState(false);
  const [crmOverride, setCrmOverride] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedModules(initialModules);
    setUsersCount(initialUsersCount);
    setBillingInterval(initialBillingInterval);
    setPreview(null);
    setError('');
    setMessage('');
    setConfirmReplace(false);
    setCrmOverride(false);
  }, [open, initialModules, initialUsersCount, initialBillingInterval]);

  useEffect(() => {
    if (!open || isPortal) return;
    getBillingModulePrices({ per_page: 100, is_active: true })
      .then((response) => {
        const rows = response.data || [];
        setModuleOptions(rows.filter((row) => row.is_public !== false));
      })
      .catch(() => setModuleOptions([]));
  }, [open, isPortal]);

  const portalModuleOptions = useMemo(() => {
    return (initialModules || []).map((code) => ({ canonical_code: code, name: code }));
  }, [initialModules]);

  const options = isPortal ? portalModuleOptions : moduleOptions;
  const crmInPaidCatalog = options.some((option) => option.canonical_code === 'crm');
  const showCrmOverride = canCrmOverride && !crmInPaidCatalog;

  const buildPayload = () => {
    const modules = [...selectedModules];
    if (includeSupport && !modules.includes('support')) {
      modules.push('support');
    }
    return {
      modules,
      users_count: Number(usersCount),
      billing_interval: billingInterval,
      confirm_replace: confirmReplace,
      ...(showCrmOverride && crmOverride ? { crm_override: true } : {}),
    };
  };

  const handlePreview = async () => {
    if (!subscription?.id) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const payload = buildPayload();
      const result = isPortal
        ? await previewPortalSubscriptionChange(customerId, subscription.id, payload)
        : await previewSubscriptionChange(subscription.id, payload);
      setPreview(result);
    } catch (err) {
      setError(userSeatLimitErrorMessage(err?.response?.data, t) || t('subscriptionChanges:previewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!subscription?.id) return;
    setLoading(true);
    setError('');
    try {
      const payload = buildPayload();
      const result = isPortal
        ? await createPortalSubscriptionChange(customerId, subscription.id, payload)
        : await createSubscriptionChange(subscription.id, payload);
      setMessage(t('subscriptionChanges:createSuccess'));
      onSuccess?.(result);
      onClose();
    } catch (err) {
      const seatMsg = userSeatLimitErrorMessage(err?.response?.data, t);
      setError(seatMsg || safeApiErrorMessage(err, t('subscriptionChanges:createFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('subscriptionChanges:changePackage')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isPortal && (
            <FormControl fullWidth>
              <InputLabel>{t('pricing:pricing_preview.modules_label')}</InputLabel>
              <Select
                multiple
                value={selectedModules}
                onChange={(e) => setSelectedModules(e.target.value)}
                input={<OutlinedInput label={t('pricing:pricing_preview.modules_label')} />}
                renderValue={(selected) => selected.join(', ')}
              >
                {options.map((option) => (
                  <MenuItem key={option.canonical_code} value={option.canonical_code}>
                    <Checkbox checked={selectedModules.includes(option.canonical_code)} />
                    <ListItemText primary={option.name || option.canonical_code} secondary={option.canonical_code} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isPortal && (
            <TextField
              label={t('pricing:pricing_preview.modules_label')}
              value={selectedModules.join(', ')}
              onChange={(e) => setSelectedModules(
                e.target.value.split(/[\n,]+/).map((v) => v.trim()).filter(Boolean),
              )}
              multiline
              minRows={2}
              fullWidth
              helperText={t('subscriptionChanges:preview')}
            />
          )}

          <TextField
            type="number"
            label={t('subscriptionChanges:usersCount')}
            value={usersCount}
            onChange={(e) => setUsersCount(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>{t('subscriptionChanges:billingInterval')}</InputLabel>
            <Select
              label={t('subscriptionChanges:billingInterval')}
              value={billingInterval}
              onChange={(e) => setBillingInterval(e.target.value)}
            >
              <MenuItem value="monthly">{t('subscriptionChanges:intervalMonthly')}</MenuItem>
              <MenuItem value="yearly">{t('subscriptionChanges:intervalYearly')}</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Checkbox checked={includeSupport} onChange={(e) => setIncludeSupport(e.target.checked)} />}
            label={t('subscriptionChanges:includeSupport')}
          />

          {showCrmOverride && (
            <FormControlLabel
              control={<Checkbox checked={crmOverride} onChange={(e) => setCrmOverride(e.target.checked)} />}
              label={t('subscriptionChanges:crmOverride')}
            />
          )}

          {preview?.pending_changes?.length > 0 && (
            <FormControlLabel
              control={<Checkbox checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} />}
              label={t('subscriptionChanges:confirmReplace')}
            />
          )}

          <Box>
            <Button variant="outlined" onClick={handlePreview} disabled={loading || selectedModules.length === 0}>
              {t('subscriptionChanges:preview')}
            </Button>
          </Box>

          <ChangePreviewPanel preview={preview} t={t} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:cancel')}</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !preview || preview.no_change}>
          {t('subscriptionChanges:createRequest')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SubscriptionChangeDetailDialog({ open, onClose, changeRequest, onRefresh }) {
  const { t } = useTranslation(['subscriptionChanges', 'moduleAccessEvents', 'common', 'billing']);
  const { session } = useAuth();
  const canApply = hasPermission(session, 'system.billing.subscription_changes.apply');
  const canManage = hasPermission(session, 'system.billing.subscription_changes.manage');

  if (!changeRequest) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('subscriptionChanges:viewDetail')} #{changeRequest.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography variant="body2">{t('subscriptionChanges:changeType')}: {t(`subscriptionChanges:${changeRequest.change_type}`)}</Typography>
          <Typography variant="body2">{t('common:status')}: {t(`subscriptionChanges:${changeRequest.status}`)}</Typography>
          <Typography variant="body2">{t('subscriptionChanges:currentPackage')}: {(changeRequest.current_modules || []).join(', ')}</Typography>
          <Typography variant="body2">{t('subscriptionChanges:requestedPackage')}: {(changeRequest.requested_modules || []).join(', ')}</Typography>
          {changeRequest.effective_at && (
            <Typography variant="body2">{t('subscriptionChanges:effectiveAt')}: {new Date(changeRequest.effective_at).toLocaleString()}</Typography>
          )}
          {changeRequest.no_refund && <Alert severity="warning">{t('subscriptionChanges:noRefundPolicy')}</Alert>}
          {changeRequest.permission_cleanup_required && (
            <Alert severity="info">{t('subscriptionChanges:permissionCleanupRequired')}</Alert>
          )}
          {changeRequest.invoice_id && (
            <Button component={RouterLink} to={`/billing/invoices/${changeRequest.invoice_id}`} size="small">
              {t('subscriptionChanges:viewInvoice')}
            </Button>
          )}
        </Stack>

        {changeRequest.module_access_events?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>{t('moduleAccessEvents:title')}</Typography>
            {changeRequest.module_access_events.map((event) => (
              <Typography key={event.id} variant="body2">
                {t(`moduleAccessEvents:${event.event_type}`, event.event_type)} — {event.module_code || '—'}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:close')}</Button>
        {canManage && changeRequest.status === 'scheduled' && (
          <Button color="warning" onClick={() => onRefresh?.('cancel')}>{t('subscriptionChanges:cancelScheduledDowngrade')}</Button>
        )}
        {canApply && ['pending_payment', 'scheduled'].includes(changeRequest.status) && (
          <Button variant="contained" onClick={() => onRefresh?.('apply')}>{t('subscriptionChanges:forceApply')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
